'use client';
import Loading from '@/app/(withLayout)/loading';
import { ConfirmationDetails } from '@/components/confirmationDetails';
import { InstallmentCard, InstallmentSummary, installmentNotes } from '@/components/installmentSummary';
import { sendOnbEvent } from '@/components/newRelic';
import { BankAccountCard, LoanSummaryCard, PaymentMethodCard, RatesCard } from '@/components/reviewCards';
import { useStepData } from '@/components/stepData';
import { setAdditionalData } from '@/redux/additionalDataSlice';
import { pushErrorReason } from '@/redux/navigationSlice';
import { RootState } from '@/redux/store';
import { Box, Stack, Typography } from '@mui/material';
import { installmentRates, reviewAndSubmit } from '@repo/onb-api';
import { COLORS, OrderCard } from '@repo/ui';
import { setClientCookie } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StepWrapper from './stepWrapper';

export default function ReviewAndSubmit({
  stepname: stepName,
  stepdata: stepDataProp,
}: {
  stepname: reviewAndSubmit['name'];
  stepdata?: reviewAndSubmit['variables'];
}): React.JSX.Element {
  const stepData = useStepData(stepDataProp);
  const dispatch = useDispatch();
  const router = useRouter();
  const errorState = useSelector((state: RootState) => state.errorState);
  const { accessKey } = useSelector((state: RootState) => state.flowdata);
  const isFundsInsufficient = errorState?.error === 'INSUFFICIENT_FUNDS';
  const {
    requestedAmount = 0,
    installmentCount = 0,
    installmentRates: { TEA: tea = 0, CFT: cft = 0, TNA: tna = 0 } = {},
    installmentAmount = 0,
    installmentFirstDueDate,
    paymentMethod,
    bankAccountReference,
    cardNumber,
    cardIssuer,
    termsAndConditions,
    paymentSchedule,
    documentNumber,
    gender,
    fullName,
    birthDate,
    taxpayerId,
    email,
    mobilePhoneNumber,
    province,
    withInterest,
    installmentPlanType,
  } = stepData || {};

  const installmentsOverride = {
    ...stepData?.installmentRates,
    CFTTEA: stepData?.installmentRates?.CFT,
  } as installmentRates;

  const isCuotas = process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE === 'onb-cuotas';
  const additionalData = useSelector((state: RootState) => state.additionalData);
  const { merchantName = '', price = '', merchantOrderId = '', policyProduct } = additionalData;
  const subtitle = isCuotas
    ? policyProduct === 'EFECTIVOC'
      ? 'Revisá tus datos antes de confirmar la operación'
      : 'Revisá tus datos antes de confirmar la operación y disfrutar tu compra.'
    : 'Revisá tus datos antes de confirmar tu préstamo y empezar a disfrutarlo.';

  useEffect(() => {
    if (isFundsInsufficient) {
      sendOnbEvent({
        type: 'error',
        subtype: 'insufficientFunds',
        severity: 'medium',
      });
    }
  }, [isFundsInsufficient]);

  useEffect(() => {
    if (!accessKey) {
      dispatch(pushErrorReason('accessKey'));
      router.push('/error');
    }
    setClientCookie('requestedAmount', `${requestedAmount}`);
    dispatch(setAdditionalData({ requestedAmount: `${requestedAmount}` }));
  }, [requestedAmount]);

  return !accessKey ? (
    <Loading />
  ) : (
    <StepWrapper>
      <Stack
        sx={{
          gap: 1,
        }}
      >
        <Typography variant="h1">Estamos casi listos</Typography>
        <Typography component="p" variant="subtitle2">
          {subtitle}
        </Typography>
      </Stack>
      {isCuotas ? (
        <>
          <OrderCard
            merchantName={merchantName || ''}
            merchantOrderId={merchantOrderId || ''}
            price={price || ''}
            isLoan={policyProduct === 'EFECTIVOC'}
          />
          <InstallmentCard>
            <InstallmentSummary
              label={policyProduct === 'EFECTIVOC' ? 'Lo pagás en' : 'Tu compra en cuotas'}
              count={installmentCount}
              amount={installmentAmount}
              withInterest={withInterest}
              notes={installmentNotes(installmentPlanType, installmentFirstDueDate)}
            />
          </InstallmentCard>
        </>
      ) : (
        <LoanSummaryCard
          requestedAmount={requestedAmount}
          installmentCount={installmentCount}
          installmentAmount={installmentAmount}
          firstDueDate={installmentFirstDueDate}
        />
      )}
      {bankAccountReference ? (
        <Box sx={{ mt: isCuotas ? 2 : 0 }}>
          <BankAccountCard cbu={bankAccountReference} />
        </Box>
      ) : (
        <></>
      )}
      <Box sx={{ mt: isCuotas && !bankAccountReference ? 2 : 0 }}>
        <PaymentMethodCard
          paymentMethod={paymentMethod}
          cardIssuer={cardIssuer}
          cardNumber={cardNumber}
          error={isFundsInsufficient}
        />
      </Box>
      {isFundsInsufficient ? (
        <Typography role="alert" variant="body2" color={COLORS.state.error}>
          Algo no funcionó con la tarjeta seleccionada. Tocá el lápiz y elegí otra para continuar.
        </Typography>
      ) : (
        <></>
      )}
      <RatesCard tea={tea} tna={tna} cft={cft} />
      <>
        {tna === 0 && (
          <Typography variant="body3">
            Préstamo con TNA = 0%: no se aplicará interés compensatorio. Sin embargo, si se registrara mora, el importe
            vencido e impago devengará un interés moratorio equivalente a una TNA=80 %, más un interés punitorio
            equivalente al cincuenta por ciento (50%) de dicha tasa moratoria.
          </Typography>
        )}
      </>
      <Typography variant="body3">
        Queremos que tengas toda la información clara antes de seguir. Al hacer “click” en el botón de “Confirmar” y
        tildar los casilleros de aceptación dispuestos a tal efecto, estás confirmando tu solicitud de préstamo a
        Crédito Directo S.A., CUIT 30-71210113-6, con domicilio en Tte. Gral. Juan Domingo Perón 646, piso 4° de la
        Ciudad Autónoma de Buenos Aires (“Directo”), los datos que proporcionaste en el proceso de solicitud y las
        condiciones particulares del préstamo que siguen a continuación (la “Solicitud”).
      </Typography>
      {/* <OnbForm<reviewAndSubmit> stepName={stepName!} submitLabel="Confirmar" submitBlacklist={['termsAndConditions']}>
        <Checkbox
          label="Acepto los términos y condiciones"
          {...connectedField({
            checkbox: true,
            stepName,
            name: 'hasTermsAccepted',
            required: true,
            beforeChange: (value) => !!value,
            errorHandler: {
              type: 'checkbox',
              errorText: 'Aceptá los términos y condiciones para continuar',
            },
          })}
        />
      </OnbForm> */}
      <ConfirmationDetails
        stepName={stepName}
        termsAndConditions={termsAndConditions}
        requestedAmount={requestedAmount}
        installmentCount={installmentCount}
        paymentSchedule={paymentSchedule}
        documentNumber={documentNumber}
        gender={gender}
        fullName={fullName}
        birthDate={birthDate}
        taxpayerId={taxpayerId}
        email={email}
        mobilePhoneNumber={mobilePhoneNumber}
        province={province}
        paymentMethod={paymentMethod}
        bankAccountReference={bankAccountReference}
        installmentRates={installmentsOverride}
        showSujetoObligado
      />
    </StepWrapper>
  );
}
